/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { UserDto } from '@/commons/dtos';
import { useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { getAribaSkuNameById, getUserRoleTranslationKeyByRoleValue } from '@/utils/utils';
import { Button, Card, FlexBox, Label, Text, Title } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useMemo } from 'react';

type Props = {
    userDto: UserDto;
    isRemoving?: boolean;

    onRemove: () => void;
    onEdit: () => void;
};

export default function CreatedUserCard({ userDto, onRemove, onEdit, isRemoving = false }: Props) {
    const { givenName, familyName, emailId, role } = userDto;
    const { t } = useTranslations();
    const { currentSKU } = useAppSelector(state => state.productList);
    const fullName = `${givenName} ${familyName}`;
    const skuName = useMemo(() => (currentSKU.SKUId ? getAribaSkuNameById(currentSKU.SKUId) : null), [currentSKU.SKUId]);

    const translatedRole = skuName ? `${skuName} ${t(getUserRoleTranslationKeyByRoleValue(role))}`.trim() : t(role);

    return (
        <Card style={{ opacity: isRemoving ? 0.5 : 1 }}>
            <FlexBox
                direction="Column"
                style={{
                    padding: ThemingParameters.sapContent_Space_S,
                    gap: 8,
                }}
            >
                <Title level="H6">{fullName}</Title>
                <FlexBox direction="Column">
                    <CreatedUserCard.Item label={t('EMAIL')} value={emailId} />
                    <CreatedUserCard.Item label={t('ROLE_LABEL')} value={translatedRole} />
                </FlexBox>

                <FlexBox justifyContent="End" style={{ gap: 8 }}>
                    <Button disabled={isRemoving} onClick={onEdit}>
                        {t('EDIT_BUTTON')}
                    </Button>
                    <Button data-testid="removeBtn" disabled={isRemoving} onClick={onRemove}>
                        {t('REMOVE_BUTTON')}
                    </Button>
                </FlexBox>
            </FlexBox>
        </Card>
    );
}

CreatedUserCard.Item = function CreatedUserCardItem({ label, value }: { label: string; value: string | number }) {
    return (
        <FlexBox style={{ gap: 8, padding: '2px 0' }}>
            <Label>{label}:</Label>
            <Text style={{ textWrap: 'nowrap' }}>{value || '-'}</Text>
        </FlexBox>
    );
};
